import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import JobCard from "./job-card";
import { PageHeaderTabs } from "features/health-safety-forms";
import { ADD_JOB_PATH } from "../actions/types";
import { Typography, TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";
import { Page, PageContent, PopupSnackbar, SendEmailModal,
	PageHeaderListActions,
} from "frame/components";
import {
	getPageResults,
	sortByPropertyDate,
	useTerminology,
	getCurrentTimestamp,
	canPinJobs,
} from "utils";
import { TASK_HOME_ROUTE } from "features/home/home-routes";
import { safetyWiseApi } from "utils/safetyWiseApi";
import InfiniteScroll from "react-infinite-scroll-component";
import { CardText } from "features/timesheet/components/timesheet-list";
import { initialJobState } from "../reducers/jobs-reducer";
import EditJobModal from "./edit-job-modal";
import { useSelector } from "react-redux";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";

const useStyles = makeStyles((theme) => ({
	root: {},
	heading: {
		marginTop: theme.spacing(4),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(0)
		}
	},
	body: {
		marginTop: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filter: { display: "flex" },
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
}));

const JobList = ({
	currentUserId,
	role,
	hasNetwork,
	isSummaryList,
	currentOrganisationGUID,
	onEdit,
	flags,
}) => {
	const classes = useStyles();
	const { siteLabel, customerLabel } = useTerminology();

	const [showEmailModal, setShowEmailModal] = useState(false);
	const [isSendingEmail, setIsSendingEmail] = useState(false);
	const [selectedItemId, setSelectedItemId] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [selectedItemType, setSelectedItemType] = useState(null);
	const [siteId, setSiteId] = useState(null);
	const [customerId, setCustomerId] = useState(null);

	const ref = useRef(null);
	const [height, setHeight] = useState(0);
	const [jobList, setJobList] = useState(getPageResults(initialJobState));
	const [hasMoreJobs, setHasMoreJobs] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [isDoneFetching, setIsDoneFetching] = useState(false);
	const [totalNumberOfRecords, setTotalNumberOfRecords] = useState(1);
	const defaultPageSize = 20;
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const isInitialMount = useRef(true);
	const lastUpdateFlagRef = useRef(flags?.update);

	const empty = jobList && jobList.length <= 0;
	const allowPin = canPinJobs(role);

	const patchJobInList = useCallback((jobId, patch) => {
		setJobList((prev) =>
			prev.map((j) => (j.id === jobId ? { ...j, ...patch } : j))
		);
	}, []);

	const buildJobPinBody = useCallback(
		(job, pinFields) => ({
			id: job.id,
			title: job.title || "",
			createdAt: job.createdAt,
			type: job.type || "",
			customerId: job.customerId || "",
			siteId: job.siteId || "",
			staffId: job.staffId || "",
			notes: job.notes || "",
			attachments: job.attachments || [],
			organisationId: job.organisationId || currentOrganisationGUID,
			latitude: job.latitude ?? "",
			longitude: job.longitude ?? "",
			timestamp: job.timestamp || getCurrentTimestamp(),
			creatorId: job.creatorId || "",
			pinnedToSite: !!job.pinnedToSite,
			pinnedToCustomer: !!job.pinnedToCustomer,
			pinnedToStaff: !!job.pinnedToStaff,
			...pinFields,
		}),
		[currentOrganisationGUID]
	);

	const submitJobPinUpdate = useCallback(
		(job, body, patch) => {
			if (!currentOrganisationGUID || !job) {
				return;
			}
			safetyWiseApi
				.post(`${currentOrganisationGUID}/job/createorupdate`, body)
				.then(() => patchJobInList(job.id, patch))
				.catch(() => {
					// eslint-disable-next-line no-alert
					window.alert(
						"Could not update pin. Check your connection and try again."
					);
				});
		},
		[currentOrganisationGUID, patchJobInList]
	);

	const handleToggleSitePin = useCallback(
		(job) => {
			const nextPinned = !job.pinnedToSite;
			submitJobPinUpdate(
				job,
				buildJobPinBody(job, { pinnedToSite: nextPinned }),
				{ pinnedToSite: nextPinned }
			);
		},
		[buildJobPinBody, submitJobPinUpdate]
	);

	const handleToggleCustomerPin = useCallback(
		(job) => {
			const nextPinned = !job.pinnedToCustomer;
			submitJobPinUpdate(
				job,
				buildJobPinBody(job, { pinnedToCustomer: nextPinned }),
				{ pinnedToCustomer: nextPinned }
			);
		},
		[buildJobPinBody, submitJobPinUpdate]
	);

	const handleToggleStaffPin = useCallback(
		(job) => {
			const nextPinned = !job.pinnedToStaff;
			submitJobPinUpdate(
				job,
				buildJobPinBody(job, { pinnedToStaff: nextPinned }),
				{ pinnedToStaff: nextPinned }
			);
		},
		[buildJobPinBody, submitJobPinUpdate]
	);

	// Hydrate the local list from Redux so offline queued items (with syncing=true) show immediately
	const storeResults = useSelector(({ jobs }) => jobs.pagination?.results || []);
	useEffect(() => {
		if (!storeResults || storeResults.length === 0) {
			return;
		}
		// Merge store results into local list by id, prefer store version (contains syncing flag)
		setJobList((prev) => {
			const existingIds = new Set((prev || []).map((p) => p.id));
			const merged = [
				...storeResults,
				...(prev || []).filter((p) => !storeResults.some((s) => s.id === p.id))
			];
			// Ensure newest first by createdAt
			return merged.sort(sortByPropertyDate("createdAt"));
		});
		// eslint-disable-next-line
	}, [storeResults]);

	const onEmail = (id, siteId, selectedItemType, customerId) => () => {
		setSelectedItemId(id);
		setSiteId(siteId);
		setCustomerId(customerId);
		setSelectedItemType(selectedItemType);
		setShowEmailModal(true);
	};

	// Initial load
	useEffect(() => {
		getJobs(1, true);
	}, []);

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount - initial load is handled by the first useEffect
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			// Reset to first page when search changes
			setCurrentPage(1);
			setJobList([]);
			setTotalNumberOfRecords(1);
			setHasMoreJobs(true);
			setIsDoneFetching(false);
			getJobs(1, true);
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchFilter]);

	// When an edit is saved via the Redux-connected EditJobModal, refresh the local infinite list
	// to reflect the updated record(s).
	useEffect(() => {
		const last = lastUpdateFlagRef.current;
		const current = flags?.update;
		if (current !== last) {
			lastUpdateFlagRef.current = current;
			if (current === "done") {
				const pageSizeToLoad = Math.max(jobList.length || 0, defaultPageSize);
				getJobs(1, true, pageSizeToLoad);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags?.update]);

    const setNewJobState = (jobResult, page) => {
		let newState = initialJobState;

		if (jobResult && jobResult.results && jobResult.results.length > 0) {
            const sortedResults = [...jobResult.results].sort(sortByPropertyDate("createdAt"));
            newState = {
                ...newState,
                ...jobResult,
                results: sortedResults
            };
		}

		newState.refreshList = jobResult.refreshList;
		newState.flag = "done";
		// If navigating to first page clear the resuls from timesheetList otherwise add to the end of it
		if (page === 1) {
			setJobList([...getPageResults(newState)]);
		} else {
			setJobList([...jobList, ...getPageResults(newState)]);
		}
		setIsDoneFetching(true);
	};

	const getJobs = (
		fromPageToRequest = currentPage, // fromPageToRequest is the page to request from the API,
		recordDeletedOrUpdated = false, // recordDeletedOrUpdated is a boolean to determine whether to overwrite or append to jobLists
		pageSize = defaultPageSize // pageSize is the number of records to load from the API, can be paramaterised to request more data.
	) => {
		if (totalNumberOfRecords !== jobList.length || recordDeletedOrUpdated) {
			if (recordDeletedOrUpdated) {
				setHasMoreJobs(true);
			}

			safetyWiseApi
				.post(`${currentOrganisationGUID}/job/paged`, {
					ascending: false,
					page: fromPageToRequest,
					pageSize,
					orderBy: "createdAt",
					searchKey: searchFilter
				})
				.then((result) => {
					setNewJobState(result.data, fromPageToRequest);

					// If more then the defaultPageSize data is requested from first page then dynamically set the current page, else set to fromPageToRequest
					if (pageSize > defaultPageSize && fromPageToRequest === 1) {
						setCurrentPage(pageSize / defaultPageSize);
					} else {
						setCurrentPage(fromPageToRequest);
					}

					setTotalNumberOfRecords(result.data.totalNumberOfRecords);
					if (result.data.totalNumberOfRecords == jobList.length + result.data.results.length) {
						setHasMoreJobs(false);
					}
				});
		} else {
			setHasMoreJobs(false);
		}
	};

	const handleApiError = (error) => {
		if (error.response && error.response.status === "401") {
			setErrorMessage("You are not authorized to perform this action.");
		} else if (error.response && error.response.status === "404") {
			setErrorMessage("The record no longer exists.");
		} else {
			setErrorMessage("Something went wrong, please refresh the page and try again.");
		}
	};

	let emailListType = "";

	if (selectedItemType === "site") {
		emailListType = "relatedToParticularSite";
	} else if (selectedItemType === "staff") {
		emailListType = "allCustomerSiteRecord";
	} else if (selectedItemType === "customer") {
		emailListType = "relatedToParticularCustomer";
	}

	// The InfiniteScroll Component requires a height for it to work  effectively and for it resize relative to the parent.
	useEffect(() => {
		const handleResize = () => {
			setHeight(ref.current.offsetHeight - 20); // reduce height by 20px ottherwise the scrollbar flows past the height of screen.
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	return (
		<Page className={classes.root} title="Photos / Notes" style={{ height: "100%" }}>
			<div ref={ref} style={{ backgroundColor: "", height: "100%", overflow: "hidden" }}>
				{/* <p>
					{totalNumberOfRecords} {jobList.length}
				</p> */}
				{/* n.o of records and length of loaded list */}

				<InfiniteScroll
					dataLength={jobList.length}
					next={() => {
						getJobs(currentPage + 1);
					}}
					hasMore={hasMoreJobs}
					loader={<CardText message={"Loading..."} />}
					height={height + "px"}
					scrollThreshold={0.99}
					style={{ paddingBottom: "10px" }}
				>
					{!isSummaryList && (
						<>
															<PageHeaderTabs
									title="Photos / Notes"
									addTabPath={ADD_JOB_PATH}
									staffUserHide={false}
									hideListTabButton={true}
									backActionPath={TASK_HOME_ROUTE}
								
						titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}>
									{/* START: Search */}
									{showFilters && (
										<div className={classes.gridContainer}>
											<Grid container spacing={2} className={classes.filterGrid}>
												<Grid item xs={12} md={6}>
													<TextField
														className={classes.search}
														id="input-with-icon-textfield"
														variant="outlined"
														label="Search"
														InputProps={{
															startAdornment: (
																<InputAdornment position="start">
																	<SearchIcon />
																</InputAdornment>
															),
															endAdornment: searchFilter && (
																<InputAdornment position="end">
																	<IconButton
																		aria-label="clear search"
																		onClick={() => {
																			setSearchFilter("");
																			setCurrentPage(1);
																			setJobList([]);
																			setTotalNumberOfRecords(1);
																			setHasMoreJobs(true);
																			setIsDoneFetching(false);
																			getJobs(1, true);
																		}}
																		edge="end"
																		size="small"
																	>
																		<ClearIcon />
																	</IconButton>
																</InputAdornment>
															)
														}}
														value={searchFilter}
														onChange={(val) => {
															const { value } = val.target;
															setSearchFilter(value);
															// Real-time search with debounce effect will handle the API call
														}}
														style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
														placeholder={`e.g. Title, Notes, ${siteLabel} Name, ${customerLabel} Name, Staff Name, Type`}
													/>
												</Grid>
											</Grid>
										</div>
									)}
									{/* END: Search */}
								</PageHeaderTabs>
						</>
					)}

					<PageContent pad>
						{isSummaryList && (
							<Typography variant="h2" className={classes.heading}>
								Photos / Notes
							</Typography>
						)}

						<div className={classes.body}>
							<p style={{ marginTop: 5 }}>
								{jobList.length > 0 ? totalNumberOfRecords + " records" : "No records loaded"}
							</p>

							{!empty &&
								jobList.map((j, index) => (
									<JobCard
										key={index}
										job={j}
										role={role}
										currentUserId={currentUserId}
										hasNetwork={hasNetwork}
										enableSitePin={
											allowPin && j.type === "site" && !!j.siteId
										}
										onToggleSitePin={
											allowPin ? handleToggleSitePin : undefined
										}
										enableCustomerPin={
											allowPin &&
											j.type === "customer" &&
											!!j.customerId
										}
										onToggleCustomerPin={
											allowPin ? handleToggleCustomerPin : undefined
										}
										enableStaffPin={
											allowPin && j.type === "staff" && !!j.staffId
										}
										onToggleStaffPin={
											allowPin ? handleToggleStaffPin : undefined
										}
										onEdit={() => onEdit(j)}
										onDelete={() => {
											safetyWiseApi
												.del(`${currentOrganisationGUID}/job/delete/${j.id}`)
												.then(() => {
													//jobIndex + 1 to get position in array, then get the next n(pageSize)TH multiple which will be the page size to load
													const pageSizeToLoad =
														Math.ceil((index + 1) / defaultPageSize) * defaultPageSize;

													getJobs(1, true, pageSizeToLoad);
												});
										}}
										onEmail={onEmail(j.id, j.siteId, j.type, j.customerId)}
									/>
								))}
						</div>

						{empty && (
							<Typography variant="body1">There are currently no photos or notes.</Typography>
						)}
					</PageContent>
					<SendEmailModal
						isOpen={showEmailModal}
						title={"Email Photos / Notes Record"}
						onClose={() => setShowEmailModal(false)}
						onSend={(selectedRecipients) => {
							setIsSendingEmail(true);
							safetyWiseApi
								.post(`${currentOrganisationGUID}/job/email/${selectedItemId}`, {
									toInternalAddresses: selectedRecipients.recipients,
									toExternalAddresses: [
										...selectedRecipients.recipientsOther,
										...selectedRecipients.recipientsCustomerSites
									]
								})
								.then(() => {
									setSuccessMessage("Emails sent successfully.");
								})
								.catch(handleApiError)
								.finally(() => {
									setIsSendingEmail(false);
									setShowEmailModal(false);
								});
						}}
						isSaving={isSendingEmail}
						siteId={siteId}
						emailListType={emailListType}
						customerId={customerId}
					/>
					<PopupSnackbar
						message={errorMessage}
						coloured
						error
						onExited={() => setErrorMessage("")}
					/>
					<PopupSnackbar
						message={successMessage}
						coloured
						success
						onExited={() => setSuccessMessage("")}
					/>
					<EditJobModal />
					{!hasMoreJobs && <CardText message={"All photos have been loaded."} />}
					{jobList.length === 0 && isDoneFetching && (
						<CardText message={"No timesheets to show."} />
					)}
				</InfiniteScroll>
			</div>
		</Page>
	);
};

JobList.propTypes = {
	currentUserId: PropTypes.string.isRequired,
	role: PropTypes.string.isRequired,
	onAdd: PropTypes.func,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onSearchChanged: PropTypes.func.isRequired,
	hasNetwork: PropTypes.bool.isRequired,
	isSummaryList: PropTypes.bool,
	pagination: PropTypes.object.isRequired,
	searchFilter: PropTypes.string,
	// onGetJobList: PropTypes.func.isRequired,
	flags: PropTypes.object.isRequired
};

export default JobList;
